package dev.lingvo.site.components.studio.compose

import dev.lingvo.shared.PixelSize
import kotlin.math.max
import kotlin.math.roundToInt
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.api.ApiConfig
import dev.lingvo.site.api.Session
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.suspendCancellableCoroutine
import org.w3c.files.Blob
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.js.Promise
import dev.lingvo.site.i18n.t

/** Decodes with cancellation ownership: a late ImageBitmap is closed without publication. */
suspend fun decodeComposeBitmap(blob: Blob, resize: PixelSize? = null): dynamic = suspendCancellableCoroutine<Any> { continuation ->
    val decode: dynamic = js("createImageBitmap")
    val options: dynamic = js("({})")
    if (resize != null) { options.resizeWidth = resize.width; options.resizeHeight = resize.height; options.resizeQuality = "high" }
    decode(blob, options).then({ bitmap: dynamic ->
        if (continuation.isActive) continuation.resume(bitmap.unsafeCast<Any>(), onCancellation = { _, value, _ -> value.asDynamic().close() })
        else bitmap.close()
    }, { failure: dynamic ->
        if (continuation.isActive) continuation.resumeWithException(IllegalStateException(tr("studio.compose.compose_browser.image_decode_failed", "Image decode failed")))
    })
}

/** Authenticated CORS fetch rejects opaque bodies and aborts when its job is cancelled. */
suspend fun fetchComposeImage(
    path: String, onBlobCountChange: (Int) -> Unit = {}, sourceSize: PixelSize? = null, outputSize: PixelSize? = null,
): dynamic {
    require(path.startsWith("/projects/") && !path.contains("..") && !path.contains("\\") &&
        !path.contains(":") && !path.startsWith("//")) { "Expected a project-relative image path" }
    val controller: dynamic = js("new AbortController()")
    val options: dynamic = js("({})")
    val headers: dynamic = js("({})")
    val token = Session.token ?: error(tr("studio.compose.compose_browser.your_session_has_ended", "Your session has ended"))
    headers.Authorization = "Bearer $token"
    options.headers = headers
    options.signal = controller.signal
    options.mode = "cors"
    options.credentials = "same-origin"
    val url = ApiConfig.baseUrl + path
    val blob: Blob = suspendCancellableCoroutine { continuation ->
        continuation.invokeOnCancellation { controller.abort() }
        window.asDynamic().fetch(url, options).then({ response: dynamic ->
            if (response.ok != true || response.type == "opaque") {
                if (continuation.isActive) continuation.resumeWithException(IllegalStateException(tr("studio.compose.compose_browser.capture_fetch_failed", "Capture fetch failed")))
            } else response.blob().then({ body: Blob ->
                if (continuation.isActive) continuation.resume(body)
            }, { _: dynamic -> if (continuation.isActive) continuation.resumeWithException(IllegalStateException(tr("studio.compose.compose_browser.capture_body_failed", "Capture body failed"))) })
        }, { _: dynamic -> if (continuation.isActive) continuation.resumeWithException(IllegalStateException(tr("studio.compose.compose_browser.capture_fetch_failed", "Capture fetch failed"))) })
    }
    val resize = if (sourceSize != null && outputSize != null) {
        require(sourceSize.width > 0 && sourceSize.height > 0 && outputSize.width > 0 && outputSize.height > 0)
        val scale = minOf(1.0, outputSize.width.toDouble() / sourceSize.width, outputSize.height.toDouble() / sourceSize.height)
        PixelSize(max(1, (sourceSize.width * scale).roundToInt()), max(1, (sourceSize.height * scale).roundToInt()))
    } else null
    onBlobCountChange(1)
    try { return decodeComposeBitmap(blob, resize) }
    finally { onBlobCountChange(-1) }
}

/** Hashes exact bytes with the browser's SHA-256 implementation. */
suspend fun composeSha256(bytes: ByteArray): String {
    val buffer = window.asDynamic().crypto.subtle.digest("SHA-256", bytes).unsafeCast<Promise<dynamic>>().await()
    val view: dynamic = js("new Uint8Array(buffer)")
    return (0 until (view.length as Int)).joinToString("") { (view[it] as Int).toString(16).padStart(2, '0') }
}

/** Constructs one upload body without base64 copies. */
fun composeBlob(bytes: ByteArray): Blob {
    return js("new Blob([bytes], {type: 'image/png'})").unsafeCast<Blob>()
}
